import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, Input, Segmented, Spin, Table, Tag, Typography, message } from 'antd'
import { AimOutlined, FileSearchOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { adminApi, type PageResult } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { scopeQuery } from '../constants/knowledgeScope'
import { PageHeader, formatDate, tablePagination, usePagedQuery } from '../components/ui'
import { useAdminAuth } from '../lib/useAdminAuth'
import './DocLocate.css'

type KnowledgeItem = {
  id: number
  title: string
  index_status: string
  updated_at: string | null
}

export default function DocLocateHome() {
  const navigate = useNavigate()
  const { ready, loggedIn } = useAdminAuth()
  const [scope, setScope] = useState<KnowledgeScope>('enterprise')
  const [keyword, setKeyword] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const scopeParams = useMemo(() => scopeQuery(scope), [scope])

  const loader = useCallback(
    (page: number, pageSize: number) =>
      adminApi
        .get<PageResult<KnowledgeItem>>('/admin/knowledge/items', {
          params: {
            ...scopeParams,
            page,
            page_size: pageSize,
            keyword: searchQ.trim() || undefined,
            index_status: 'indexed',
          },
        })
        .then((r) => r.data),
    [scopeParams, searchQ],
  )

  const { items, total, loading, page, setPage, handlePaginationChange, paginationOptions } =
    usePagedQuery<KnowledgeItem>(loader)

  const openDoc = (item: KnowledgeItem) => {
    if (item.index_status !== 'indexed') {
      message.warning('文档尚未完成索引，请先在知识库中构建索引')
      return
    }
    navigate(`/locate/${item.id}?scope=${scope}`)
  }

  if (!ready) {
    return (
      <div className="doc-locate-home">
        <Spin />
      </div>
    )
  }

  if (!loggedIn) {
    return (
      <div className="doc-locate-home">
        <PageHeader title="文档定位" description="按模板解析文档字段，并在 PDF / Word 原文中标红定位。" />
        <Alert type="info" message="请先登录管理账号后使用文档定位（需配置对话模型与解析模板）" showIcon />
      </div>
    )
  }

  return (
    <div className="doc-locate-home public-page-scroll">
      <PageHeader
        title="文档定位"
        description="选择已索引文档，按解析模板抽取字段结果，点击条目在原文中标红定位。"
      />

      <div className="doc-locate-hero">
        <div className="doc-locate-hero-icon">
          <AimOutlined />
        </div>
        <div className="doc-locate-hero-text">
          <span className="doc-locate-hero-title">选择文档开始定位</span>
          <span className="doc-locate-hero-desc">
            支持 PDF、Word；定位模板在管理后台「文档定位」维护
          </span>
        </div>
      </div>

      <div className="doc-locate-home-panel">
        <div className="doc-locate-home-toolbar">
          <Segmented
            value={scope}
            onChange={(v) => {
              setScope(v as KnowledgeScope)
              setPage(1)
            }}
            options={[
              { label: '企业知识库', value: 'enterprise' },
              { label: '个人知识库', value: 'personal' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索文档标题"
            style={{ width: 260 }}
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onSearch={(v) => {
              setSearchQ(v)
              setPage(1)
            }}
          />
        </div>

        <Table
          rowKey="id"
          size="middle"
          loading={loading}
          dataSource={items}
          pagination={tablePagination(page, total, handlePaginationChange, paginationOptions)}
          locale={{ emptyText: '暂无已索引文档，请先在知识库上传并完成索引' }}
          columns={[
            {
              title: '文档标题',
              dataIndex: 'title',
              ellipsis: true,
              render: (t: string, r) => t || `文档 #${r.id}`,
            },
            {
              title: '索引状态',
              dataIndex: 'index_status',
              width: 100,
              render: (v: string) => (
                <Tag color={v === 'indexed' ? 'green' : 'default'}>{v === 'indexed' ? '已索引' : v}</Tag>
              ),
            },
            {
              title: '更新时间',
              dataIndex: 'updated_at',
              width: 168,
              render: (v: string | null) => formatDate(v) || '—',
            },
            {
              title: '操作',
              width: 120,
              render: (_: unknown, r: KnowledgeItem) => (
                <Button type="primary" size="small" icon={<FileSearchOutlined />} onClick={() => openDoc(r)}>
                  打开定位
                </Button>
              ),
            },
          ]}
        />
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          仅列出已完成索引的文档；未索引条目请返回知识库构建索引后再试。
        </Typography.Text>
      </div>
    </div>
  )
}
